import { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Page sectioning, borrowed from banking-app information rhythm (the HSBC
 * Pay screen was the reference): a bold section heading with a hairline
 * running to the right, then content — most often a 2-up TileGrid. The
 * heading + hairline is what turns "a column of cards" into a page with
 * structure, which is most of what made the dashboards feel plain.
 */
export function Section({
  title,
  action,
  children,
  className,
}: {
  title: string;
  /** Optional right-aligned affordance, e.g. a "View all" link. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      <div className="flex items-center gap-3">
        <h2 className="h-card text-base text-foreground shrink-0">
          {title}
        </h2>
        <div className="h-px flex-1 bg-border" aria-hidden="true" />
        {action}
      </div>
      {children}
    </section>
  );
}

/** 2-up on mobile, 4-up on desktop. The HSBC grids are always 2-up. */
export function TileGrid({
  children,
  className,
  columns = 2,
}: {
  children: ReactNode;
  className?: string;
  /** Desktop column count; mobile is always 2. */
  columns?: 2 | 3 | 4;
}) {
  const desktop = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' }[columns];
  return <div className={cn('grid grid-cols-2 gap-3', desktop, className)}>{children}</div>;
}

export type TileTint = 'teal' | 'amber' | 'clay' | 'ink' | 'none';

/* Domain-bound tints (see --tint-* in index.css):
   teal = work, amber = people, clay = money, ink = neutral.
   'none' drops the tinted square entirely — the duotone icons carry their
   own clay accent, and a tinted square behind a duotone mark doubles the
   colour and muddies both (the reference tiles sit icons straight on
   white). */
const TINTS: Record<Exclude<TileTint, 'none'>, string> = {
  teal: 'bg-kanfy-tint-teal text-kanfy-tint-teal-fg',
  amber: 'bg-kanfy-tint-amber text-kanfy-tint-amber-fg',
  clay: 'bg-kanfy-tint-clay text-kanfy-tint-clay-fg',
  ink: 'bg-kanfy-tint-ink text-kanfy-tint-ink-fg',
};

/**
 * Icon-left / label-right action tile. Deliberately quiet: white surface,
 * hairline border, contact shadow only — the banking-tile idiom relies on
 * repetition and alignment for its polish, not per-tile decoration.
 */
export function ActionTile({
  icon,
  label,
  sub,
  tint = 'ink',
  onClick,
  chevron = false,
  compact = false,
}: {
  icon: ReactNode;
  label: string;
  /** Optional second line, muted. */
  sub?: string;
  tint?: TileTint;
  onClick: () => void;
  /** Trailing chevron for tiles that read as navigation. */
  chevron?: boolean;
  /**
   * Below sm, stack the icon over a small label so four tiles fit in one row
   * (pair with a four-column TileGrid there). From sm up it is the ordinary
   * tile. For a page where these are secondary shortcuts and a phone's first
   * screen is better spent on the work itself.
   */
  compact?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center rounded-2xl bg-card border border-border/70 shadow-e1 hover:shadow-e2 hover:-translate-y-px active:translate-y-0 active:shadow-e1 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        compact
          ? 'flex-col justify-start gap-1.5 px-1 py-2.5 text-center sm:flex-row sm:gap-3 sm:p-4 sm:text-left'
          : 'gap-3 p-3.5 sm:p-4 text-left',
      )}
    >
      <div
        className={cn(
          'rounded-xl flex items-center justify-center shrink-0 text-foreground',
          compact ? 'w-8 h-8 sm:w-10 sm:h-10' : 'w-10 h-10',
          tint !== 'none' && TINTS[tint],
        )}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className={cn('min-w-0', compact ? 'w-full sm:w-auto sm:flex-1' : 'flex-1')}>
        <p
          className={cn(
            'font-semibold text-foreground',
            compact ? 'text-[11px] leading-tight break-words sm:text-sm sm:leading-snug' : 'text-sm leading-snug',
          )}
        >
          {label}
        </p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
      </div>
      {chevron && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />}
    </button>
  );
}
